
import { defineComponent, onMounted, reactive, computed, toRefs } from "vue";
import html2pdf from "html2pdf.js";
import { fetchSensors, fetchCommissioningReport } from "@/helpers/api";
import { Sensor } from "@/interfaces/Sensor";

export default defineComponent({
  name: "CommissioningReportModal",
  emits: ["close", "save"],
  setup(props, { emit }) {
    const state = reactive({
      loading: true as boolean,
      generating: false as boolean,

      sensors: [] as Sensor[],
      selectedAsset: null as string | null,

      errorMsg: "" as string,
    });

    // Turbines aren't looked up from the systems DB - the Asset tag on sensors (eleven DB) is
    // what already associates a sensor with a turbine (see condition_matching.py's
    // split_by_asset), so the picker is built the same way BurstsPage.vue builds its
    // Site/Aggregator/NodeName filters: distinct tag values pulled from /api/v1/sensors.
    const assets = computed(() => {
      const values = state.sensors.map((sensor) => sensor.Tags?.Asset).filter((asset): asset is string => !!asset);
      return new Set(values.sort());
    });

    const loadSensors = async () => {
      state.loading = true;
      try {
        state.sensors = await fetchSensors();
      } catch (error) {
        state.errorMsg = error instanceof Error ? error.message : "Failed to load sensors";
      } finally {
        state.loading = false;
      }
    };

    const generate = async () => {
      if (!state.selectedAsset) {
        state.errorMsg = "Select a turbine first";
        return;
      }

      state.errorMsg = "";
      state.generating = true;

      try {
        const html = await fetchCommissioningReport(state.selectedAsset);

        // Same source as the picker (already-loaded sensor tags), so this doesn't need its
        // own request - just the first sensor for this asset that happens to carry a Site tag.
        const site = state.sensors.find((sensor) => sensor.Tags?.Asset === state.selectedAsset && sensor.Tags?.Site)
          ?.Tags?.Site;
        const namePart = [state.selectedAsset, site].filter(Boolean).join(" ");
        // Filesystem-illegal characters only; spaces and hyphens are kept so the name stays readable.
        const filenameSafeName = namePart.replace(/[\\/:*?"<>|]/g, "").trim();

        await html2pdf()
          .from(html)
          .set({ filename: `Turbine Commissioning report - ${filenameSafeName}.pdf` })
          .save();

        emit("save");
      } catch (error) {
        state.errorMsg = error instanceof Error ? error.message : "Failed to generate report";
      } finally {
        state.generating = false;
      }
    };

    const close = () => {
      emit("close");
    };

    onMounted(loadSensors);

    return {
      ...toRefs(state),
      assets,
      generate,
      close,
    };
  },
});
